import '../free-trial/signedout-availability-free-trial.display';
import './../product-card/product-card';
import emit from '../../common/events';
import mapMagentoProductsToCardProducts from './cart-list/mapping/cart-list.mapping';
import store, { waitForUser } from '../../store/store';
import { ActionStatus } from '../../common/constants/action-status';
import { BaseComponent } from '../base-component/base-component';
import { MessageType } from '../alert/global-message.interface';
import { Product } from './shop-cart/interfaces/shop-cart.interface';
import { ProductCardData } from '../product-card/product-card-display';
import { StoreState } from '../../store/store.interface';
import { booleanConverter } from '../../common/converters';
import { cleanFreeTrialReplacedFlag } from '../free-trial/utils/free-trial-replacement.util';
import { connect } from 'pwa-helpers';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
  getCart,
  getCartContext,
  getCartStatus,
  getCartType,
  getIfSharpenPlusAvailableInCart,
  isB2cK12Storefront,
} from './shop-cart/store/shop-cart.selectors';
import { getCartPageUrl, isHomeschooler, isK12Admin } from '../../common/utils/cart-util';
import { getShopCart } from './shop-cart/thunks/get-shop-cart.thunk';
import { html, css, nothing } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { isAleksLooseleaf, isHigheredEbook, isTunnelPurchaseFlow } from '../../common/utils/util';
import { getUserDataForUserAnalytics, isSuccessUser, selectUser } from '../user-profile/user-profile.selectors';
import { loading } from '../../common/constants/api-constants';
import { getBraintreeClientTokenApiStatus, shippingMethodSelected, updatePaymentDetails, updatePoOrderNumber, updatePurchaseOrderApplied, updatePurchaseOrderSelected } from '../checkout/shop-checkout';
import {
  FreeTrialPurchaseConverter,
  shouldConvertFreeTrialForInternational
} from '../free-trial/utils/free-trial-conversion.util';
import './../upsell/sharpen-upsell';
import { getProductTitleWithoutEdition } from '../../common/utils/product-util';
import { checkCartContainsSharpenPlusAndIsNationalOrTunnel } from './cart-list/utils/cart-list-util';
import { AlertSize } from '../alert/alert';
import variables from '../../common/styles/variables';
import { PaymentDetails, PaymentType } from '../checkout/shop-checkout.interface';
import { resetPurchaseOrderNumber } from '../../common/utils/payment-util';
import { PO_ERROR_MESSAGE } from '../payment-methods/utils/po-order.util';
import { focusCartCardAfterRemoval, handleRemovedFromCart } from './utils/cart-a11y-util';

/**
 * An element to show products in your cart.
 *
 * @slot empty - If the empty slot is used, the emptyMessage property is ignored.
 *
 * @csspart empty - Each product entry in the cart list. This is only used if the empty slot is not used.
 * @csspart entry - The container of the cart list.
 */
@customElement('mhecomm-cart-list')
export class CartList extends connect(store)(BaseComponent) {
  static styles = [variables,
    css`
      :host {
        display: block;
      }
      .item-list {
        list-style-type: none;
        padding: 0 !important;
      }
      .item-list li.large-card {
        padding-bottom: 0.5rem;
      }
      .loading-container {
        display: grid;
        grid-template-columns: 2fr 7.5fr;
        gap: 10px;
      }
      .cart-list-heading {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }
      .purchase-order-wrapper {
        background: var(--mh-light-blue-2) !important;
        border-color: var(--mh-light-blue-1) !important;
      }
    `,
  ];

  /**
   * The message to display when the cart is empty. If the empty slot is provided, this property is ignored.
   */
  @property()
  emptyMessage = 'Your cart is empty';

  /**
   * Whether or not the empty slot should be used. If false, the emptyMessage will be used instead of the empty slot.
   */
  @property({ converter: booleanConverter })
  useEmptySlot = false;

  /**
   * Which store to show a cart for. If not set it will show the national cart.
   */
  @property()
  store?: string;

  @property({ converter: booleanConverter })
  checkoutPreview = false;

  @state()
  private products: Array<ProductCardData> = [];

  @state()
  private quantity!: number;

  @state()
  private itemsLoading = false;

  @state()
  private itemsText = '';

  @state()
  private status: ActionStatus = ActionStatus.idle;

  @state()
  private displayShowMore = true;

  @state()
  private viewItemsExpanded = false;

  /**
   * Flag to indicate if the user has selected to proceed with purchase order payment.
   */
  @state()
  private isPurchaseOrderSelected = false;

  @state()
  private isPurchaseOrderApplied = false;

  /**
   * Braintree Client token to initialize Purchase Order Payment.
   */
  @state()
  private clientToken: string = '';

  /**
   * The message to announce when a product is removed from the cart.
   */
  @state()
  private cartRemoveMessage = '';

  @state()
  private poNumber: string | undefined;

  @state()
  private poNumberIsValid = true;

  @query('#purchase-order-number')
  private poInput?: HTMLInputElement;

  private collapse: any;

  private isSuccessUser = false;

  private cartHasK12Products = false;

  private isK12Admin = false;

  private isK12Homeschooler = false;

  private freeTrialPurchaseConverter: FreeTrialPurchaseConverter;

  constructor() {
    super();
    this.freeTrialPurchaseConverter = new FreeTrialPurchaseConverter();
  }

  /**
   * The index of the product that was removed from the cart.
   */
  private pendingFocusIndex: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(this, 'reload-cart-list', this.reloadCartList);
    this.addTrackedEventListener(this, 'removed-from-cart', this.onRemovedFromCart.bind(this));

    /**
     * Reset the shipping method, when redirected to cart page
     */
    store.dispatch(shippingMethodSelected(undefined));

    // Name space the cart with a unique ID so that other components do not reset the cart to the national cart... or something
    if (!getCart(store.getState() as StoreState)) {
      store.dispatch(getShopCart({ store: this.store }));
    }
  }

  /**
   * Handles announcement and focus whenever an item is removed from the cart.
   *
   * @param event
   */
  private onRemovedFromCart(event: Event): void {
    handleRemovedFromCart(
      event,
      this.products,
      isTunnelPurchaseFlow(),
      (i) => { this.pendingFocusIndex = i; },
      (m) => { this.cartRemoveMessage = m; },
      this.updateComplete
    );
  }

  /**
   * @param removedIndex
   */
  private moveFocusAfterRemoval(removedIndex: number): void {
    focusCartCardAfterRemoval(
      this.renderRoot,
      removedIndex,
      this.products.length,
      () => {
        // Cart is now empty — focus the empty-state message so the screen reader announces it.
        const emptyEl = this.renderRoot.querySelector<HTMLElement>('[part="empty"]');
        if (emptyEl) {
          emptyEl.focus();
        }
      }
    );
  }

  stateChanged(state: StoreState) {
    const previousLength = this.products.length;
    const cart = getCart(state);
    const count = cart?.productCount ?? 0;
    const products = cart?.products ?? [];

    if (count !== this.quantity && count > 0) {
      this.convertProducts(products);
    }

    this.products = mapMagentoProductsToCardProducts(products, this.store);
    this.quantity = count;
    this.itemsText = this.quantity + ' ' + (this.quantity > 1 ? 'items' : 'item');

    if (this.pendingFocusIndex !== null && this.products.length < previousLength) {
      const idx = this.pendingFocusIndex;
      this.pendingFocusIndex = null;
      this.updateComplete.then(() => this.moveFocusAfterRemoval(idx));
    }

    const newStatus = getCartStatus(state);
    if (newStatus === ActionStatus.succeeded || this.products.length > 0) {
      this.status = ActionStatus.succeeded;
      if (newStatus === ActionStatus.succeeded) {
        cleanFreeTrialReplacedFlag();
      }
    } else if (this.status === ActionStatus.idle && newStatus === ActionStatus.loading) {
      this.status = ActionStatus.loading;
    }

    if (getCartStatus(state) === ActionStatus.failed) {
      this.status = ActionStatus.failed;
      return;
    }
    this.isSuccessUser = isSuccessUser(state);

    this.cartHasK12Products =
      this.products?.length > 0 &&
      this.products.filter((product) => product.businessUnit.toUpperCase() == 'SEG').length > 0;
    this.isK12Admin = isK12Admin(getCart(state));
    this.isK12Homeschooler = isHomeschooler(getCart(state));

    this.poNumber = state.shopCheckoutState.poOrderNumber;

    waitForUser().then(() => {
      if(this.isSuccessUser) {
        if (getBraintreeClientTokenApiStatus(state) === ActionStatus.succeeded) {
          this.clientToken = state.shopCheckoutState.clientToken.data?.token || "";
        }
      }
    });
  }

  renderComp() {
    return html`
      <span aria-live="assertive" aria-atomic="true" class="visually-hidden">${this.cartRemoveMessage}</span>
      ${this.renderCartContent()}
    `;
  }

  private renderCartContent() {
    if (this.status === ActionStatus.idle || this.status === ActionStatus.loading || this.itemsLoading) {
      return this.loadingTemplate();
    } else if (this.checkoutPreview) {
      if (isTunnelPurchaseFlow() && !this.isUpsellProductInCart()) {
        return this.loadProductInfo();
      }
      return this.loadCheckoutPreviewHtml();
    } else {
      return this.loadCartHtml();
    }
  }

  isUpsellProductInCart() {
    // currently validate only if sharpen plus is available in cart
    // because thats the only upsell
    // later will extend this function
    // and create a full blown util
    return getIfSharpenPlusAvailableInCart(store.getState() as StoreState);
  }

  loadProductInfo() {
    if (this.products.length) {
      return html`<mhecomm-product-info-display .product=${this.products[0]}></mhecomm-product-info-display>`;
    }
    return nothing;
  }

  loadCheckoutPreviewHtml() {
    return html`
      <div class="cart-list-heading">
        <div>
          <h2 class="fs-4 fw-semibold mb-0">
            Cart Summary
            <span class="fs-6 text-nowrap">(${this.itemsText})</span>
          </h2>
        </div>
        ${!this.isAleksLooseleafPurchaseForCart() ? html`<a
          id="edit-cart-link"
          @click=${this._onClickEditCartLink}
          class="edit-cart-link"
          href="${this.getUserCartPageUrl()}"
          >Edit Cart</a>`
        : nothing}
      </div>
      <hr aria-hidden="true" />
      <div part="container" class="cart-list">
        ${this.products?.length === 0
          ? html`
              ${this.useEmptySlot
                ? html` <slot name="empty"></slot> `
                : html` <p class="fs-3" part="empty" tabindex="-1">${this.emptyMessage}</p> `}
            `
          : ''}
        ${this.products?.length > 0
          ? html`
              <div class="d-none d-md-block">
                <ul class="item-list">
                  ${this.products.map(
                    (product, index) => html`
                      <li class="small-card mb-3 ${index > 3 ? (this.displayShowMore ? 'd-none' : '') : ''}">
                        <mhecomm-product-card-display
                          part="entry"
                          variation="small-card"
                          .product=${product}
                          .isCartCard=${true}
                          ?hideCoverImage=${isHigheredEbook(product)}
                        >
                        </mhecomm-product-card-display>
                        ${!isLastItem(index, this.products?.length) ? html`<hr/>`: nothing}
                      </li>
                    `
                  )}
                </ul>
                ${this.products?.length > 4
                  ? html`
                      <div class="text-center">
                        <a
                          class="show-more-link ${!this.displayShowMore ? 'd-none' : ''}"
                          @click=${this._toggleListDisplay}
                          href="javascript:;"
                          >Show more items in my cart</a>
                        <a
                          class="show-less-link ${this.displayShowMore ? 'd-none' : ''}"
                          @click=${this._toggleListDisplay}
                          href="javascript:;"
                          >Show less items in my cart</a
                        >
                      </div>
                    `
                  : ''}
              </div>
              <div class="d-block d-md-none">
                <a
                  role="button"
                  class="text-decoration-none"
                  data-bs-target="#mobileListSection"
                  aria-controls="mobileListSection"
                  data-bs-toggle="collapse"
                  href="javascript:;"
                  aria-expanded="${this.viewItemsExpanded}"
                  id="viewItemsLink"
                  @click=${this._handleCollapseToggle}
                  >View items in my cart
                  ${this.uxtIconReady
                    ? html`<uxt-icon
                        id="enterDropDownIcon"
                        class="ms-1"
                        icon="${this.viewItemsExpanded ? 'chevron-up-sm' : 'chevron-down-sm'}"
                      ></uxt-icon>`
                    : ''}
                </a>
                <ul class="item-list collapse" id="mobileListSection">
                  ${this.products.map(
                    (product, index) => html`
                      <li class="small-card">
                        <mhecomm-product-card-display
                          part="entry"
                          variation="small-card"
                          .product=${product}
                          .isCartCard=${true}
                          ?hideCoverImage=${isHigheredEbook(product)}
                        >
                        </mhecomm-product-card-display>
                        ${!isLastItem(index, this.products?.length) ? html`<hr/>`: nothing}
                      </li>
                    `
                  )}
                </ul>
              </div>
              ${checkCartContainsSharpenPlusAndIsNationalOrTunnel(this.products) ?
                html`
                  <hr/>
                  <mhecomm-sharpen-legal-disclaimer
                    ?isSharpenPlus=${true}
                  >
                  </mhecomm-sharpen-legal-disclaimer>
                ` :
                nothing
              }
            `
          : ''}
      </div>
    `;
  }

  updated() {
    this.collapse = this.bsInit('#mobileListSection', window.bootstrap?.Collapse, { toggle: false })?.instance;
  }

  _toggleListDisplay() {
    this.displayShowMore = !this.displayShowMore;
  }

  _handleCollapseToggle() {
    this.collapse.toggle();
    this.viewItemsExpanded = !this.viewItemsExpanded;
  }

  getUserCartPageUrl() {
    return getCartPageUrl(
      selectUser(store.getState() as StoreState),
      getCartContext(store.getState() as StoreState, this.store).storeCode
    );
  }

  isAleksLooseleafPurchaseForCart() {
    return (this.products.filter(product => isAleksLooseleaf(product))).length > 0;
  }

  _loadPriceBanner() {
    return this.isSuccessUser ? this._loadLoggedInBanner() : this._loadNotLoggedInBanner();
  }

  _loadLoggedInBanner() {
    return this.cartHasK12Products && (this.isK12Admin || this.isK12Homeschooler)
      ? html`<mhecomm-alert
          type="${MessageType.success}"
          message="Pricing for school accounts has been applied to the items in your cart."
        ></mhecomm-alert>`
      : ``;
  }

  _loadNotLoggedInBanner() {
    return this.cartHasK12Products && !isB2cK12Storefront(store.getState() as StoreState)
      ? html`<mhecomm-school-pricing-alert></mhecomm-school-pricing-alert>`
      : nothing;
  }

  _onClickEditCartLink() {
    emit({
      element: this,
      name: 'click-edit-cart-link',
      dataLayer: true,
    });
  }

  _loadPurchaseOrder() {
    return this.isSuccessUser 
      ? this._loadPurchaseOrderPaymentOption() 
      : nothing;
  }

  renderPurchaseOrderAlert() {
    return this.poNumber && this.isPurchaseOrderApplied 
      ? html `
          <mhecomm-alert
            id="purchase-order-number-success-message"
            type="${MessageType.success}"
            size=${AlertSize.small}
            ?dismissible="${false}"
            message="You can continue to Proceed to Checkout"
          ></mhecomm-alert>`
      : nothing;
  }

  renderPurchaseOrderInput() {
    return this.isPurchaseOrderSelected || this.poNumber
      ? html`
        <div class="my-1">
          <small>Enter your Purchase Order (PO) number below.</small>
        </div>
        <label 
          class="form-label" 
          for="purchase-order-number">
          Purchase Order Number <span aria-hidden="true">*</span><span class="fw-normal fst-italic">(Required)</span>
        </label>
        <input 
          class="form-control ${!this.poNumberIsValid ? 'is-invalid' : ''}"
          type="text"  
          id="purchase-order-number" 
          name="purchaseOrderNumber"
          .value="${this.poNumber || ""}" 
          @input=${(_e: InputEvent) => this.updatePurchaseOrderNumber((_e.target as HTMLInputElement).value)} 
          data-automation-id="purchase-order-number"
          aria-describedby="${!this.poNumberIsValid && `purchase-order-number-error-message`}"
          aria-required="true"
          aria-invalid="${!this.poNumberIsValid}"
          required>
        ${!this.poNumberIsValid
          ? html `
              <p 
                id="purchase-order-number-error-message"
                data-automation-id="purchase-order-number-error-message"
                class='invalid-feedback mt-2 d-block'>
                ${this.uxtIconReady ? html`<uxt-icon class="me-2" icon="exclamation-circle"></uxt-icon>` : ''}Please enter a Purchase Order Number to proceed
              </p>`
          : nothing
        }
        <button
          class="btn btn-primary w-fit-content mb-3 ${this.poNumberIsValid ? 'mt-4' : ''}"
          aria-label="Apply purchase order"
          @click=${this.onClickApplyPurchaseOrder}
          aria-describedby="${this.poNumberIsValid && `purchase-order-number-success-message`}"
          part="button"
          data-automation-id="apply-purchase-order"
          ?disabled=${false}
        >
          Apply Purchase Order
        </button>
        <div id="po-status-live-region" role="status" aria-live="polite" aria-atomic="true">
          ${this.renderPurchaseOrderAlert()}
        </div>`
      : nothing ;
  }

  _loadPurchaseOrderPaymentOption() {
    return this.cartHasK12Products && this.isK12Admin && this.clientToken
      ? html`
          <div class="card purchase-order-wrapper rounded-3 my-3 p-3" id="purchase-order-section">
            <p class="mb-2">Check the box below to pay with Purchase Order (PO)</p>
            <div class="form-check">
              <label 
                class="fs-medium fw-bold" 
                for="pay-with-purchase-order">
                Pay with Purchase Order
              </label>
              <input 
                class="form-check-input"
                type="checkbox"
                id="pay-with-purchase-order"
                name="payWithPurchaseOrder"
                aria-label="Pay with purchase order"
                @change="${this.modifyPuchaseOrder}"
                data-automation-id="pay-with-purchase-order"
                ?checked="${this.isPurchaseOrderSelected || !!this.poNumber}">
            </div>
            ${this.renderPurchaseOrderInput()}
          </div>`
      : ``;
  }

  onClickApplyPurchaseOrder() {
    this.poInput?.focus();
    this.poNumberIsValid = this.poNumber === '' || this.poNumber === undefined ? false : true;
    if (this.poNumber) {
      const paymentDetails: PaymentDetails = {
        paymentType: PaymentType.PURCHASE_ORDER
      };
      this.isPurchaseOrderApplied = true;
      store.dispatch(updatePurchaseOrderApplied(true));
      store.dispatch(updatePaymentDetails(paymentDetails));
    }

    if (!this.poNumberIsValid) {
      this.emitError(PO_ERROR_MESSAGE);
    }
    
    this.fireAnalytics('Apply Purchase Order');
  }

  resetPurchaseOrder() {
    this.poNumber = "";
    this.poNumberIsValid = true;
    this.isPurchaseOrderApplied = false;
    resetPurchaseOrderNumber();
  }

  modifyPuchaseOrder(event: InputEvent) {
    this.isPurchaseOrderSelected = (event.target as HTMLInputElement).checked;
    store.dispatch(updatePurchaseOrderSelected(this.isPurchaseOrderSelected));
    this.resetPurchaseOrder();

    if (this.isPurchaseOrderSelected) {
      this.updateComplete.then(() => this.poInput?.focus());
    }

    this.fireAnalytics(`Pay with Purchase Order ${this.isPurchaseOrderSelected ? 'on' : 'off'}`);
  }

  updatePurchaseOrderNumber(poNumber: string) {
    this.isPurchaseOrderApplied = false;
    store.dispatch(updatePoOrderNumber(poNumber));
    if (poNumber === "") {
      this.resetPurchaseOrder();
    }
  }

  loadCartHtml() {
    return html`
      ${this._loadPriceBanner()}
      ${this.quantity > 0 ? html` <h2 class="fs-4 fw-semibold">Items (${this.quantity})</h2> ` : ''}
      <div part="container" class="cart-list">
        ${this.products?.length === 0
          ? html`
              ${this.useEmptySlot
                ? html` <slot name="empty"></slot> `
                : html` <p class="fs-3" part="empty" tabindex="-1">${this.emptyMessage}</p> `}
            `
          : ''}
        ${this.products?.length > 0
          ? html`
              <ul class="item-list">
                ${this.products.map(
                  (product) => html`
                    <li class="large-card">
                      <mhecomm-product-card-display
                        part="entry"
                        store=${ifDefined(this.store)}
                        variation="large-card"
                        .product=${{
                          ...product,
                          title: getProductTitleWithoutEdition({
                            title: product.title,
                            programSourceId: product?.programSourceId!,
                            businessUnit: product.businessUnit,
                            isVirtual: product.isVirtual ? true : false,
                            formatTypeDesc: product?.formatTypeDesc!,
                            duration :product?.duration!,
                            durationPeriod: product?.durationPeriod!,
                            ...((product?.offerType) && {offerType: product?.offerType}),
                          }),
                        }}
                        .isCartCard=${true}
                        ?hideCoverImage=${isHigheredEbook(product)}
                      >
                      </mhecomm-product-card-display>
                    </li>
                  `
                )}
              </ul>
            `
          : ''}
      </div>
      ${this._loadPurchaseOrder()}
    `;
  }

  loadingTemplate() {
    return html`
      <span class="visually-hidden">Loading cart list</span>
      ${loading({ width: '50%', m: '' })}
      <hr aria-hidden="true" />
      <div class="loading-container mb-4">
        ${loading({ height: '180px' })}
        <div>
          ${loading({ height: '1.25rem', width: '70%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '40%' })}
        </div>
      </div>
    `;
  }

  /**
   * Forces the component to update the cart. This is useful if the cart was updated outside of ecomm-ui and then client code needs to tell ecomm-ui cart to update itself.
   */
  updateCart() {
    store.dispatch(getShopCart({ store: this.store }));
  }

  /**
   * Converts free trial products to purchasable products if any.
   *
   * @param products
   * @private
   */
  private convertProducts(products: Product[]) {
    const convertedProducts = this.freeTrialPurchaseConverter.convertProducts(
      products,
      shouldConvertFreeTrialForInternational()
    );

    if (convertedProducts.length > 0) {
      this.itemsLoading = true;
      this.freeTrialPurchaseConverter.purchaseProducts(convertedProducts).finally(() => {
          this.itemsLoading = false;
      });
    }
  }

  /**
   * Triggers the loading template for the cart list.
   *
   * @param event
   */
  private reloadCartList(event: CustomEventInit): void {
    this.itemsLoading = true;

    setTimeout((): void => {
      this.itemsLoading = false;
    }, event.detail.timeout ?? 500);
  }

  private emitError(label: string): void {
    emit({
      element: this,
      name: 'Error',
      detail: {
        label
      }
    });
  }

  /**
  * Emits an event when the po checkbox / apply button is clicked.
  */
  private fireAnalytics(label: string): void {
    const state = store.getState() as StoreState;

    emit({
      element: this,
      name: 'click',
      detail: {
        label,
        eventName: 'click-po-link',
        orderType: getCartType(state),
        url: window.location.href,
        user: getUserDataForUserAnalytics(state),
        comp: 'mhecomm-purchase-order-code',
        id: this.id
      },
      dataLayer: true
    });
  }
}

function isLastItem(index: number, totalItems: number) {
  return index === totalItems - 1;
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-list': CartList;
  }
}
